import { useEffect, useState } from "react";
import { GradingData } from "../../../views/GradingCenterView";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import { StyleSheet, View, TextInput, TouchableOpacity, Platform, Linking, Alert } from "react-native";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useGetAssignmentAndSubmissionBySubmissionIdQuery } from "../../../graphql/generated/graphql";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Sentry from "../../../utils/sentry";
import Folder from "../../../components/svgs/common/Folder";
import { useAppConfig } from "../../../AppConfigProvider";

const AssignmentSubmissionGradingModalContainer = ({
  isVisible,
  onClose,
  gradingData,
  onSave,
  isSubmitting,
}: {
  isVisible: boolean;
  onClose: () => void;
  gradingData: GradingData | null;
  onSave: (gradingData: GradingData, newScore: number, feedback: string) => Promise<void>;
  isSubmitting: boolean;
}) => {
  const [grade, setGrade] = useState<string>("");
  const [feedback, setFeedback] = useState<string>("");
  const appConfig = useAppConfig();
  const currentDomain = appConfig?.currentDomain || "";

  const { data, loading, error } = useGetAssignmentAndSubmissionBySubmissionIdQuery({
    variables: { submissionId: gradingData?.id || "" },
    skip: !gradingData?.id,
  });

  const assignment = data?.getAssignmentAndSubmissionBySubmissionId?.data[0]?.assignment;
  const submission = data?.getAssignmentAndSubmissionBySubmissionId?.data[0]?.submission;

  useEffect(() => {
    if (data?.getAssignmentAndSubmissionBySubmissionId?.data[0]?.submission) {
      const submissionGrade = data.getAssignmentAndSubmissionBySubmissionId.data[0].submission.grade;
      // Ensure grade is not null before calling toString()
      setGrade(submissionGrade !== null && submissionGrade !== undefined ? submissionGrade.toString() : "0");
    } else {
      // Default to "0" if no submission data
      setGrade("0");
    }

    if (data?.getAssignmentAndSubmissionBySubmissionId?.data[0]?.submission?.feedback) {
      setFeedback(data.getAssignmentAndSubmissionBySubmissionId.data[0].submission.feedback);
    } else {
      setFeedback("");
    }
  }, [data]);

  const handleSave = async () => {
    if (!gradingData) return;

    const newScore = Number(grade);

    if (Number.isNaN(newScore)) {
      Alert.alert("Invalid score", "Please enter a valid number");
      return;
    }

    if (newScore < 0 || newScore > 100) {
      Alert.alert("Invalid score", "Please enter a score between 0 and 100");
      return;
    }

    await onSave(gradingData, newScore, feedback);
  };

  const handleOpenURL = async (url) => {
    const fullUrl = url.startsWith("/") ? `https://${currentDomain}${url}` : url;

    if (Platform.OS === "web") {
      try {
        const token = await AsyncStorage.getItem("token");

        // Create a form that posts to the URL
        const form = document.createElement("form");
        form.method = "POST";
        form.action = fullUrl;
        form.target = "_blank";

        // Add token as hidden field
        const tokenInput = document.createElement("input");
        tokenInput.type = "hidden";
        tokenInput.name = "token";
        tokenInput.value = token;
        form.appendChild(tokenInput);

        // Submit form
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      } catch (err) {
        console.error("Failed to open URL:", err);
        Sentry.captureException(err);
      }
    } else {
      // For mobile platforms
      try {
        const token = await AsyncStorage.getItem("token");

        // Make POST request
        const response = await fetch(fullUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: `token=${encodeURIComponent(token)}`,
        });

        // Get the final URL after redirects
        const finalUrl = response.url;

        // Open the final URL in the device's browser
        await Linking.openURL(finalUrl);
      } catch (err) {
        console.error("Failed to open URL:", err);
        Sentry.captureException(err);
      }
    }
  };

  if (loading) {
    return (
      <CustomModal
        visible={isVisible}
        onRequestClose={onClose}
        title="Loading..."
        showCloseButton
        style={styles.modalContent}
        exitFill="#6B7280"
        onShow={() => {}}
        ariaLabel="Loading Assignment Modal">
        <CustomText text="Loading assignment details..." size="m" useTranslationText={false} />
      </CustomModal>
    );
  }

  if (error || !assignment) {
    return (
      <CustomModal
        visible={isVisible}
        onRequestClose={onClose}
        title="Error"
        showCloseButton
        style={styles.modalContent}
        exitFill="#6B7280"
        onShow={() => {}}
        ariaLabel="Error Modal">
        <CustomText text="Failed to load assignment details." size="m" useTranslationText={false} />
      </CustomModal>
    );
  }

  return (
    <CustomModal
      visible={isVisible}
      onRequestClose={onClose}
      title={assignment.name || "Assignment"}
      showCloseButton
      style={styles.modalContent}
      exitFill="#6B7280"
      onShow={() => {}}
      ariaLabel="Assignment Details Modal">
      <CustomText
        text={assignment.description || "No description available"}
        size="m"
        style={styles.description}
        useTranslationText={false}
      />

      <View style={styles.submissionSection}>
        <CustomText
          text="Student Submission"
          size="l"
          weight="bold"
          style={styles.sectionTitle}
          useTranslationText={false}
        />

        <View style={styles.fileUploadContainer}>
          <View style={styles.fileHeaderRow}>
            <View style={styles.iconTextRow}>
              <Folder />
              <CustomText text="File upload" size="m" weight="bold" useTranslationText={false} />
            </View>
          </View>
          {submission?.files && submission.files.length > 0 ? (
            submission.files.map((file) => (
              <TouchableOpacity
                style={styles.dateResubmitRow}
                onPress={() => handleOpenURL(file.fileURL)}
                key={file.id}>
                <CustomText
                  text={file.fileName || "Unnamed file"}
                  size="m"
                  style={styles.fileName}
                  useTranslationText={false}
                />
                <CustomText
                  text={submission.completedAt ? new Date(submission.completedAt).toLocaleDateString() : "Date unknown"}
                  size="s"
                  style={styles.date}
                  useTranslationText={false}
                />
              </TouchableOpacity>
            ))
          ) : (
            <CustomText text="No files uploaded" size="m" style={styles.noFiles} useTranslationText={false} />
          )}
        </View>

        <View style={styles.gradingSection}>
          <View style={styles.gradeContainer}>
            <CustomText text="Grade %" size="m" weight="bold" style={styles.label} useTranslationText={false} />
            <TextInput
              style={styles.gradeInput}
              value={grade}
              onChangeText={setGrade}
              keyboardType="numeric"
              placeholder="Enter grade percentage"
            />
          </View>

          <View style={styles.feedbackContainer}>
            <CustomText
              text="Feedback for the Student (Optional)"
              size="m"
              weight="bold"
              style={styles.label}
              useTranslationText={false}
            />
            <TextInput
              style={styles.feedbackInput}
              value={feedback}
              onChangeText={setFeedback}
              multiline
              numberOfLines={4}
              placeholder="Enter feedback"
            />
          </View>
        </View>
      </View>

      <View style={styles.modalFooter}>
        <View style={styles.buttonGroup}>
          <CustomButton text="Cancel" styleType="secondary" onPress={onClose} useTranslationText={false} />
          <CustomButton
            text="Save"
            styleType="primary"
            onPress={handleSave}
            useTranslationText={false}
            disabled={isSubmitting}
          />
        </View>
      </View>
    </CustomModal>
  );
};

export default AssignmentSubmissionGradingModalContainer;

const styles = StyleSheet.create({
  modalContent: {
    maxHeight: "90%",
    width: "80%",
    padding: 24,
  },
  description: {
    color: "#6B7280",
    marginBottom: 32,
    lineHeight: 24,
  },
  submissionSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    marginBottom: 16,
  },
  fileUploadContainer: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  fileHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  iconTextRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  dateResubmitRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  fileName: {
    color: "#111827",
  },
  date: {
    color: "#6B7280",
  },
  noFiles: {
    color: "#6B7280",
    fontStyle: "italic",
    textAlign: "center",
    marginTop: 8,
  },
  gradingSection: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    padding: 16,
  },
  gradeContainer: {
    marginBottom: 16,
  },
  feedbackContainer: {
    marginBottom: 16,
  },
  label: {
    marginBottom: 8,
  },
  gradeInput: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
  },
  feedbackInput: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    minHeight: 120,
    textAlignVertical: "top",
  },
  modalFooter: {
    flexDirection: "row",
    marginTop: 24,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 16,
  },
  buttonGroup: {
    flexDirection: "row",
    gap: 12,
  },
});
